import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { reorderRestaurantMenuItems, createMenuItem, updateMenuItem, deleteMenuItemImage, deleteMenuItem, fetchMenuItems, fetchMenuTypes } from '../../api/restaurantApi';
import { Box, CardContent, Typography, Button, CircularProgress, Tooltip, Grid, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Chip, Stack } from '@mui/material';
import { Utensils, Camera, Plus } from 'lucide-react';
import { deleteRemovedImages } from '../../utils/imageUtils';
import useImageChangeHandler from '../../hooks/useImageChangeHandler';
import MenuCard from '../ui/Card/MenuCard';
import EditableText from '../ui/EditableField/EditableText';
import ImageUploadDialog from '../ui/ImageUpload/ImageUploadDialog';
import RestaurantMenuFormModal from './RestaurantMenuFormModal';
import RestaurantMenuShimmer from './RestaurantMenuShimmer';

const mockMenu = [
  {
    name: 'Salmon tartare',
    description: 'avocado, toasts',
    weight: '190 g',
    price: 15.99,
    image: 'https://images.rawpixel.com/image_png_800/czNmcy1wcml2YXRlL3Jhd3BpeGVsX2ltYWdlcy93ZWJzaXRlX2NvbnRlbnQvbHIvcGYtczg3LW1uLTI1LTAxLnBuZw.png',
  },
  {
    name: 'Bruschetta with mozzarella',
    description: 'baked tomatoes, guacamole',
    weight: '190 g',
    price: 6.55,
    image: 'https://kalpak-travel.com/wp-content/uploads/2022/01/centralasia-food-besh-barmak.jpg',
  },
  {
    name: 'Bruschetta with shrimps',
    description: 'tomatoes, avocado',
    weight: '180 g',
    price: 12.56,
    image: '',
  },
];

const truncateWords = (text, limit = 20) => {
  const words = text.split(' ');
  return words.length > limit ? words.slice(0, limit).join(' ') + '...' : text;
};

function SortableMenuItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
    zIndex: isDragging ? 10 : 'auto'
  };

  return children({
    setNodeRef,
    style,
    attributes,
    listeners,
    isDragging
  });
}

export default function RestaurantMenu({ menu = [], propertyId, restaurantId, onImageUpdate, loading }) {
  console.log('[RestaurantMenu] props:', {
    restaurantId,
    propertyId,
    menuType: typeof menu,
    menuLength: Array.isArray(menu) ? menu.length : 'n/a',
    first: Array.isArray(menu) && menu.length > 0 ? menu[0] : null
  });
  const [uploadTarget, setUploadTarget] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [menuState, setMenuState] = useState(menu);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [menuTypes, setMenuTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const handleEditItem = (item) => setEditingItem(item);
  const handleCloseModal = () => setEditingItem(null);

  const handleSaveItem = (savedItem) => {
    // This handler is called after the modal saves (for both create and update).
    // It receives the final item data from the API.
    const itemExists = menuState.some(item => item._id === savedItem._id);

    if (itemExists) {
      // This was an update. Replace the item in the state.
      setMenuState(prevState =>
        prevState.map(item => (item._id === savedItem._id ? savedItem : item))
      );
    } else {
      // This was a new item. The `handleAddMenu` function might have added a temporary item.
      // We need to replace the temporary item with the real one.
      const tempItemIndex = menuState.findLastIndex(item => String(item._id).startsWith('temp-'));
      if (tempItemIndex > -1) {
        setMenuState(prevState => {
          const newState = [...prevState];
          newState[tempItemIndex] = savedItem;
          return newState;
        });
      } else {
        // If no temp item, just add it to the end.
        setMenuState(prevState => [...prevState, savedItem]);
      }
    }
    upsertMenuType(savedItem?.type);
  };

  // FX state: which cards are saving (spinner overlay) and which should highlight briefly
  const [savingIds, setSavingIds] = useState(new Set());
  const [highlightIds, setHighlightIds] = useState(new Set());

  const addSaving = (id) => setSavingIds(prev => new Set(prev).add(String(id)));
  const removeSaving = (id) => setSavingIds(prev => { const n = new Set(prev); n.delete(String(id)); return n; });
  const addHighlight = (id) => setHighlightIds(prev => new Set(prev).add(String(id)));
  const removeHighlight = (id) => setHighlightIds(prev => { const n = new Set(prev); n.delete(String(id)); return n; });

  const listRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const normalizeType = useCallback((value) => {
    if (!value || typeof value !== 'string') return 'food';
    const trimmed = value.trim();
    return trimmed.length ? trimmed : 'food';
  }, []);

  const upsertMenuType = useCallback((value) => {
    const normalized = normalizeType(value);
    setMenuTypes(prev => {
      if (prev.some(t => t.toLowerCase() === normalized.toLowerCase())) {
        return prev;
      }
      return [...prev, normalized];
    });
  }, [normalizeType]);

useEffect(() => {
  if (Array.isArray(menu) && menu.length > 0) {
    console.log('🧪 Synced menu from props (len):', menu.length);
    setMenuState(menu);
    return;
  }
  // Fallback: fetch directly if parent didn't pass items yet
  if (restaurantId && propertyId) {
    console.log('🧪 Fallback fetchMenuItems because props.menu is empty');
    fetchMenuItems(restaurantId, propertyId)
      .then(({ data }) => {
        const next = Array.isArray(data?.menu) ? data.menu : (Array.isArray(data) ? data : []);
        console.log('🧪 fetchMenuItems result length:', next.length);
        setMenuState(next);
      })
      .catch(err => console.error('❌ fetchMenuItems failed:', err));
  }
}, [menu, restaurantId, propertyId]);

useEffect(() => {
  if (!propertyId) return;
  fetchMenuTypes(propertyId)
    .then(({ data }) => {
      const incoming = (data?.types || []).map(t => normalizeType(t?.name || t));
      setMenuTypes(prev => {
        const next = [...prev];
        incoming.forEach(name => {
          if (!next.some(existing => existing.toLowerCase() === name.toLowerCase())) {
            next.push(name);
          }
        });
        return next;
      });
    })
    .catch(err => console.error('❌ Failed to fetch menu types:', err));
}, [propertyId, normalizeType]);

useEffect(() => {
  if (!Array.isArray(menuState)) return;
  menuState.forEach(item => upsertMenuType(item?.type));
}, [menuState, upsertMenuType]);

useEffect(() => {
  setSelectedTypes(prev => {
    if (prev.length === 0) return prev;
    const available = new Set(
      menuTypes.map(type => normalizeType(type).toLowerCase())
    );
    const filtered = prev.filter(typeValue => available.has(typeValue));
    if (filtered.length === prev.length && filtered.every((value, index) => value === prev[index])) {
      return prev;
    }
    return filtered;
  });
}, [menuTypes, normalizeType]);

  const enrichedMenu = (Array.isArray(menuState) && menuState.length > 0
    ? menuState
    : (Array.isArray(menu) ? menu : mockMenu)
  ).map(raw => {
    const item = raw || {};
    console.log('🧪 Rendering menu item:', {
      name: item.name,
      price: item.price,
      promotion: item.promotion,
      promotionType: typeof item.promotion,
      promotionFields: item.promotion && typeof item.promotion === 'object'
        ? Object.keys(item.promotion)
        : null
    });
    return {
      ...item,
      restaurantId: item.restaurantId || restaurantId
    };
  });
  console.log('[RestaurantMenu] render menuState length:', Array.isArray(menuState) ? menuState.length : 'n/a');

  const handleImageChange = useImageChangeHandler(uploadTarget, setSelectedFile, setPreviewImage);

  const sortedTypes = React.useMemo(() => {
    const unique = new Map();
    menuTypes.forEach(type => {
      if (!type) return;
      const normalized = normalizeType(type);
      const key = normalized.toLowerCase();
      if (!unique.has(key)) {
        unique.set(key, normalized);
      }
    });
    return Array.from(unique.values()).sort((a, b) => a.localeCompare(b));
  }, [menuTypes, normalizeType]);

  const visibleMenu = React.useMemo(() => {
    if (selectedTypes.length === 0) return enrichedMenu;
    const allowed = new Set(selectedTypes);
    return enrichedMenu.filter(item => allowed.has(normalizeType(item?.type).toLowerCase()));
  }, [enrichedMenu, selectedTypes, normalizeType]);

  const handleFilterChange = (event) => {
    let { value } = event.target;
    if (typeof value === 'string') {
      value = value.split(',');
    }
    if (value.includes('__all')) {
      setSelectedTypes([]);
      return;
    }
    const normalized = value
      .map(item => normalizeType(item).toLowerCase())
      .filter(Boolean);
    const unique = Array.from(new Set(normalized));
    setSelectedTypes(unique);
  };

  const handleAddMenu = () => {
    // This will open the RestaurantMenuFormModal in 'create' mode
    // because the initialValues prop will be an empty object.
    if (selectedTypes.length === 1) {
      const selectedValue = selectedTypes[0];
      const label = sortedTypes.find(type => type.toLowerCase() === selectedValue) || selectedValue;
      setEditingItem({ type: label });
      return;
    }
    setEditingItem({});
  };

  const handleDuplicate = async (src) => {
    if (!src) return;
    const tempId = `temp-dup-${Date.now()}`;
    const optimistic = {
      ...src,
      _id: tempId,
      name: `${src.name || 'Item'} (copy)`,
      restaurantId,
      propertyId,
    };
    addSaving(tempId);
    addHighlight(tempId);
     setMenuState(prev => {
   const idx = prev.findIndex(it => String(it._id) === String(src._id));
   const insertAt = idx >= 0 ? idx + 1 : prev.length;
   const next = [...prev.slice(0, insertAt), optimistic, ...prev.slice(insertAt)];
   // Scroll the newly created card into view next to the original
  setTimeout(() => {
     const el = document.querySelector(`[data-menu-id=\"${tempId}\"]`);
     if (el && typeof el.scrollIntoView === 'function') {
       el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    } else if (listRef.current) {
       listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
     }
  }, 0);
   return next;
 });

    try {
      const payload = {
        name: src.name ? `${src.name} (copy)` : 'New item (copy)',
        description: src.description || '',
        weight: src.weight || '',
        price: typeof src.price === 'number' ? src.price : (parseFloat(src.price) || 0),
        image: src.image || '',
        propertyId,
        insertAfterId: src._id,
      };
      const res = await createMenuItem(restaurantId, payload);
      const created = res?.data?.item || res?.data || null;
      if (created && created._id) {
        setMenuState(prev => prev.map(it => (it._id === tempId ? { ...created, restaurantId } : it)));
        removeSaving(tempId);
        // Highlight the persisted card briefly
        if (created && created._id) {
          addHighlight(created._id);
          setTimeout(() => removeHighlight(created._id), 1200);
        }
      }
    } catch (err) {
      console.error('❌ Failed to duplicate menu item:', err);
      removeSaving(tempId);
      removeHighlight(tempId);
      setMenuState(prev => prev.filter(it => it._id !== tempId));
    }
  };

  function ConfirmDialog({ open, title, description, onCancel, onConfirm, loading }) {
    return (
      <Dialog open={open} onClose={onCancel}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography>{description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>Cancel</Button>
          <Button onClick={onConfirm} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (loading) {
    return <RestaurantMenuShimmer />;
  }

  return (
    <Box sx={{ p: 1 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ px: 1, pb: 1 }}
      >
        <Typography variant="h6">Menu Items</Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="restaurant-menu-type-filter-label">Filter by type</InputLabel>
          <Select
            labelId="restaurant-menu-type-filter-label"
            multiple
            value={selectedTypes}
            label="Filter by type"
            onChange={handleFilterChange}
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip size="small" label="All types" />
                  </Stack>
                );
              }
              return (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {selected.map(value => {
                    const label = sortedTypes.find(type => type.toLowerCase() === value) || value;
                    return <Chip key={value} size="small" label={label} />;
                  })}
                </Stack>
              );
            }}
          >
            <MenuItem value="__all">
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label="All types" />
              </Stack>
            </MenuItem>
            {sortedTypes.map(type => {
              const value = type.toLowerCase();
              return (
                <MenuItem key={value} value={value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={type} />
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Stack>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!over || active.id === over.id) return;
          const oldIndex = menuState.findIndex(item => String(item._id) === active.id);
          const newIndex = menuState.findIndex(item => String(item._id) === over.id);
          const reordered = arrayMove(menuState, oldIndex, newIndex);
          setMenuState(reordered);

          reorderRestaurantMenuItems(
            restaurantId,
            propertyId,
            reordered // send full objects; backend sanitizes promotion/id
          ).catch(err => console.error('⚠️ Failed to reorder restaurant menu:', err));
        }}
      >
        <SortableContext
          items={visibleMenu.map(item => String(item._id))}
          strategy={rectSortingStrategy}
        >
          <Box
            ref={listRef}
            sx={{
              pt: 2,
              pb: 2,
              height: 'calc(100vh - 200px)',
              overflowY: 'auto'
            }}
          >
            <Grid
              container
              spacing={3}
              justifyContent="center"
              alignItems="stretch"
              sx={{
                maxWidth: '100%',
                mx: 'auto'
              }}
            >
              {visibleMenu.length === 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      py: 6,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: 'text.secondary'
                    }}
                  >
                    <Typography variant="body2">
                      No menu items match this type yet.
                    </Typography>
                  </Box>
                </Grid>
              )}
              {visibleMenu.map((item, index) => (
                <Grid item xs={6} sm={4} md={3} key={item._id}>
                  <SortableMenuItem id={String(item._id)}>
                    {({ setNodeRef, style, attributes, listeners }) => (
                      <Box
                        ref={setNodeRef}
                        style={style}
                        sx={{
                          width: '100%',
                          maxWidth: 280,
                          mx: 'auto',
                          position: 'relative',
                          cursor: 'default',
                          // Highlight pulse when recently duplicated
                          ...(highlightIds.has(String(item._id)) && {
                            boxShadow: 6,
                            borderRadius: 3,
                            border: '2px solid',
                            borderColor: 'primary.main',
                            animation: 'pulseHighlight 900ms ease-in-out',
                          }),
                          '@keyframes pulseHighlight': {
                            '0%': { transform: 'scale(0.98)' },
                            '50%': { transform: 'scale(1.01)' },
                            '100%': { transform: 'scale(1)' }
                          }
                        }}
                        data-menu-id={String(item._id)}
                      >
                        {/* Saving overlay */}
                        {savingIds.has(String(item._id)) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              bgcolor: 'rgba(255,255,255,0.6)',
                              zIndex: 5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 3,
                            }}
                          >
                            <CircularProgress size={28} />
                          </Box>
                        )}
                        {item.promotion && typeof item.promotion === 'object' && item.promotion.title && (
                          (() => {
                            console.log('✅ Showing promotion badge for', item.name, item.promotion.title);
                            const now = new Date();
                            const start = new Date(item.promotion.startDate);
                            const end = new Date(item.promotion.endDate);
                            let promoColor = 'error.main';
                            if (item.promotion.status === 'inactive') {
                              promoColor = 'grey.600';
                            } else if (now < start) {
                              promoColor = 'info.main'; // upcoming
                            } else if (now > end) {
                              promoColor = 'grey.400'; // expired
                            }
                            if (item.promotion.status !== 'inactive' && now > end) {
                              return null;
                            }
                            return (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  backgroundColor: promoColor,
                                  color: 'white',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  zIndex: 1
                                }}
                              >
                                {item.promotion.title}
                                {item.promotion.discount ? ` - ${item.promotion.discount}% OFF` : ''}
                              </Box>
                            );
                          })()
                        )}
                        <MenuCard
                          item={item}
                          draggableProps={{ ...attributes, ...listeners }}
                          onImageClick={() => {
                            setUploadTarget({
                              ...item,
                              index,
                              restaurantId: item.restaurantId || uploadTarget?.restaurantId || restaurantId,
                              propertyId
                            });
                            setPreviewImage(item.image || null);
                            setSelectedFile(item.image || null);
                          }}
                          onDelete={() => { setItemToDelete(item); setDeleteOpen(true); } }
                          onEdit={handleEditItem}
                          onDuplicate={() => handleDuplicate(item)}
                        >
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Box>
                              <EditableText
                                key={`${item._id}-name-${item.name}`}
                                value={item.name || '—'}
                                onSave={(val) => {
                                  setMenuState(prev => prev.map(t => t._id === item._id ? { ...t, name: val } : t));
                                  updateMenuItem(restaurantId, item._id, { name: val, propertyId });
                                }}
                                variant="subtitle1"
                                fontWeight="bold"
                              />
                              {item.description?.split(' ').length > 8 ? (
                                <Box sx={{ mt: 1, position: 'relative', '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: 0,
                                  right: 0,
                                  width: '100%',
                                  height: '1.5em',
                                  background: 'linear-gradient(to bottom, rgba(255,255,255,0), #fff 90%)',
                                  pointerEvents: 'none'
                                } }}>
                                  <Tooltip title={item.description || '—'}>
                                    <span>
                                      <EditableText
                                        key={`${item._id}-desc-${item.description}`}
                                        value={item.description || '—'}
                                        displayValue={truncateWords(item.description || '', 8)}
                                        onSave={(val) => {
                                          setMenuState(prev => prev.map(t => t._id === item._id ? { ...t, description: val } : t));
                                          updateMenuItem(restaurantId, item._id, { description: val, propertyId });
                                        }}
                                        variant="body2"
                                        color="text.secondary"
                                        gutterBottom
                                      />
                                    </span>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Box sx={{ mt: 1 }}>
                                  <EditableText
                                    key={`${item._id}-desc-${item.description}`}
                                    value={item.description || '—'}
                                    onSave={(val) => {
                                      setMenuState(prev => prev.map(t => t._id === item._id ? { ...t, description: val } : t));
                                      updateMenuItem(restaurantId, item._id, { description: val, propertyId });
                                    }}
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                  />
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {item.weight}
                              </Typography>
                              <EditableText
                                key={`${item._id}-price-${item.price}`}
                                value={item.price ? item.price.toFixed(2) : '—'}
                                type="number"
                                onSave={(val) => {
                                  const newPrice = parseFloat(val);
                                  setMenuState(prev => prev.map(t => t._id === item._id ? { ...t, price: newPrice } : t));
                                  updateMenuItem(restaurantId, item._id, { price: newPrice, propertyId });
                                }}
                                variant="body1"
                                fontWeight="bold"
                              />
                            </Box>
                          </CardContent>
                        </MenuCard>
                      </Box>
                    )}
                  </SortableMenuItem>
                </Grid>
              ))}
              {/* Add Menu inline tile */}
              <Grid item xs={6} sm={4} md={3} key="add-menu-tile">
                <Box sx={{ width: '100%', maxWidth: 280, mx: 'auto', height: '100%' }}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={handleAddMenu}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAddMenu(); }}
                    sx={{
                      border: '2px dashed',
                      borderColor: 'grey.300',
                      borderRadius: 2,
                      height: '100%',
                      minHeight: 330,
                      minWidth: 240,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Plus size={36} />
                    <Typography variant="subtitle1" sx={{ mt: 1 }}>
                      Add Menu
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </SortableContext>
      </DndContext>
      <ImageUploadDialog
        open={!!uploadTarget}
        uploadTarget={uploadTarget}
        previewImage={previewImage}
        uploading={uploading}
        setUploading={setUploading}
        setUploadTarget={setUploadTarget}
        setSelectedFile={setSelectedFile}
        setPreviewImage={setPreviewImage}
        handleImageChange={handleImageChange}
        onSave={async (updatedImage) => {
          try {
            const updatePayload = { image: updatedImage, propertyId };
            const updateResult = await updateMenuItem(uploadTarget.restaurantId, uploadTarget._id, updatePayload);
            if (typeof onImageUpdate === 'function') {
              onImageUpdate(uploadTarget._id, updatedImage);
            }
            deleteRemovedImages(
              uploadTarget?.image ? [uploadTarget.image] : [],
              updatedImage ? [updatedImage] : []
            );
          } catch (err) {
            console.error('❌ Error while updating menu item or cleaning up:', err);
          }
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        title="Delete Menu Item"
        description={itemToDelete ? `Are you sure you want to delete "${itemToDelete.name || 'this item'}"? This action cannot be undone.` : ''}
        onCancel={() => { setDeleteOpen(false); setItemToDelete(null); }}
        onConfirm={async () => {
          if (!itemToDelete?._id) return;
          setDeleting(true);
          try {
            await deleteMenuItem(restaurantId, itemToDelete._id, propertyId);
            setMenuState(prev => prev.filter(it => it._id !== itemToDelete._id));
          } catch (err) {
            console.error('❌ Failed to delete menu item:', err);
          } finally {
            setDeleting(false);
            setDeleteOpen(false);
            setItemToDelete(null);
          }
        }}
        loading={deleting}
      />
      <RestaurantMenuFormModal
        open={!!editingItem}
        onClose={handleCloseModal}
        onSave={handleSaveItem}
        initialValues={editingItem}
        restaurantId={restaurantId}
        propertyId={propertyId}
      />
    </Box>
  );
}
